/**
 * /accounting/gl/recurring — Recurring journal entry templates.
 *
 * Wave C UI for the RecurringJournalContext. List, create, run-due, and
 * deactivate templates. Posting goes through GeneralLedgerContext with
 * per-run idempotency so it's safe to click "Run Due" twice.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Play, Plus, PowerOff, RefreshCw } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import {
  useRecurringJournals,
  type RecurringCadence,
  type RecurringLine,
} from "@/data/accounting/recurring/RecurringJournalContext";
import { toast } from "@/hooks/use-toast";

const CADENCE_LABEL: Record<RecurringCadence, string> = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  annually: "Annually",
};

const fmt = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });

export default function RecurringJournalsPage() {
  const { accounts } = useGeneralLedger();
  const { templates, createTemplate, deactivate, runDue, runNow } = useRecurringJournals();
  const [showNew, setShowNew] = useState(false);

  const activeAccounts = useMemo(
    () => accounts.filter(a => a.active).sort((a, b) => a.number.localeCompare(b.number)),
    [accounts],
  );

  const dueCount = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return templates.filter(t => t.active && t.nextRunDate <= today).length;
  }, [templates]);

  const handleRunDue = () => {
    try {
      const { posted, skipped } = runDue();
      toast({
        title: "Recurring journals run",
        description: `${posted.length} posted, ${skipped} skipped (closed periods).`,
      });
    } catch (e: any) {
      toast({ title: "Run failed", description: e.message, variant: "destructive" });
    }
  };

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link to="/accounting/gl" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2">
            <ArrowLeft className="h-3 w-3" /> Back to General Ledger
          </Link>
          <h1 data-tour-id="recurring-list" className="text-2xl font-semibold tracking-tight">Recurring Journal Entries</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Templates that auto-post on a cadence — rent, depreciation, accruals. Idempotent: rerunning is safe.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <Button size="sm" variant="outline" onClick={handleRunDue} disabled={dueCount === 0}>
            <RefreshCw className="h-4 w-4 mr-1" />Run Due ({dueCount})
          </Button>
          <Button size="sm" onClick={() => setShowNew(true)}>
            <Plus className="h-4 w-4 mr-1" />New Template
          </Button>
        </div>
      </div>

      <CockpitSection>
        <div className="text-xs text-muted-foreground mb-3">
          {templates.length} total · {templates.filter(t => t.active).length} active · {dueCount} due now
        </div>

        {templates.length === 0 ? (
          <div className="text-sm text-muted-foreground p-6 text-center">
            No recurring templates yet. Create one for monthly rent, depreciation, or accrued expenses.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Cadence</TableHead>
                <TableHead>Next Run</TableHead>
                <TableHead>Last Run</TableHead>
                <TableHead className="text-right">Runs</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map(t => {
                const total = t.lines.filter(l => l.side === "debit")
                  .reduce((s, l) => s + l.amount, 0);
                const today = new Date().toISOString().slice(0, 10);
                const isDue = t.active && t.nextRunDate <= today;
                return (
                  <TableRow key={t.id}>
                    <TableCell>
                      <div className="font-medium">{t.name}</div>
                      <div className="text-xs text-muted-foreground">{t.description}</div>
                    </TableCell>
                    <TableCell>{CADENCE_LABEL[t.cadence]}</TableCell>
                    <TableCell>
                      <span className={isDue ? "text-primary font-medium" : ""}>
                        {t.nextRunDate}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {t.lastRunAt ? t.lastRunAt.slice(0, 10) : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{t.totalRuns}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(total)}</TableCell>
                    <TableCell>
                      {t.active
                        ? <Badge variant="outline">Active</Badge>
                        : <Badge variant="secondary">Inactive</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost"
                        disabled={!t.active}
                        onClick={() => {
                          try {
                            runNow(t.id);
                            toast({ title: `Posted ${t.name}` });
                          } catch (e: any) {
                            toast({ title: "Failed", description: e.message, variant: "destructive" });
                          }
                        }}>
                        <Play className="h-3 w-3 mr-1" />Run
                      </Button>
                      {t.active && (
                        <Button size="sm" variant="ghost" onClick={() => deactivate(t.id)}>
                          <PowerOff className="h-3 w-3 mr-1" />Stop
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CockpitSection>

      {showNew && (
        <NewTemplateDialog
          accounts={activeAccounts}
          onClose={() => setShowNew(false)}
          onCreate={(input) => {
            try {
              createTemplate(input);
              toast({ title: "Template created" });
              setShowNew(false);
            } catch (e: any) {
              toast({ title: "Could not create", description: e.message, variant: "destructive" });
            }
          }}
        />
      )}
    </CockpitPage>
  );
}

function NewTemplateDialog({
  accounts, onClose, onCreate,
}: {
  accounts: ReturnType<typeof useGeneralLedger>["accounts"];
  onClose: () => void;
  onCreate: (input: {
    name: string; description: string; cadence: RecurringCadence;
    nextRunDate: string; lines: RecurringLine[];
  }) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [cadence, setCadence] = useState<RecurringCadence>("monthly");
  const [nextRunDate, setNextRunDate] = useState(new Date().toISOString().slice(0, 10));
  const [lines, setLines] = useState<RecurringLine[]>([
    { accountId: accounts[0]?.id ?? "", amount: 0, side: "debit", memo: "" },
    { accountId: accounts[0]?.id ?? "", amount: 0, side: "credit", memo: "" },
  ]);

  const dr = lines.filter(l => l.side === "debit").reduce((s, l) => s + (l.amount || 0), 0);
  const cr = lines.filter(l => l.side === "credit").reduce((s, l) => s + (l.amount || 0), 0);
  const balanced = Math.round(dr * 100) === Math.round(cr * 100) && dr > 0;

  const updateLine = (i: number, patch: Partial<RecurringLine>) => {
    setLines(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  };
  const addLine = () => setLines(prev => [...prev, { accountId: accounts[0]?.id ?? "", amount: 0, side: "debit", memo: "" }]);
  const removeLine = (i: number) => setLines(prev => prev.filter((_, idx) => idx !== i));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>New Recurring Template</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Monthly Rent" />
            </div>
            <div>
              <Label>Cadence</Label>
              <Select value={cadence} onValueChange={(v) => setCadence(v as RecurringCadence)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annually">Annually</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} />
            </div>
            <div>
              <Label>First / Next Run Date</Label>
              <Input type="date" value={nextRunDate} onChange={e => setNextRunDate(e.target.value)} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Lines</Label>
              <Button size="sm" variant="outline" onClick={addLine}>
                <Plus className="h-3 w-3 mr-1" />Add line
              </Button>
            </div>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-4">
                    <Select value={l.accountId} onValueChange={v => updateLine(i, { accountId: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        {accounts.map(a => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.number} — {a.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Select value={l.side} onValueChange={v => updateLine(i, { side: v as "debit" | "credit" })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="debit">Debit</SelectItem>
                        <SelectItem value="credit">Credit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Input type="number" step="0.01" value={l.amount}
                      onChange={e => updateLine(i, { amount: parseFloat(e.target.value) || 0 })} />
                  </div>
                  <div className="col-span-3">
                    <Input value={l.memo ?? ""} placeholder="Memo"
                      onChange={e => updateLine(i, { memo: e.target.value })} />
                  </div>
                  <div className="col-span-1">
                    <Button size="sm" variant="ghost" onClick={() => removeLine(i)} disabled={lines.length <= 2}>×</Button>
                  </div>
                </div>
              ))}
            </div>
            <div className={`mt-3 text-xs ${balanced ? "text-muted-foreground" : "text-destructive"}`}>
              DR {fmt(dr)} · CR {fmt(cr)} {balanced ? "· balanced" : "· out of balance"}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!name || !balanced}
            onClick={() => onCreate({ name, description, cadence, nextRunDate, lines })}
          >
            Create Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
